import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/tresh.svg';
import useMonitoringSchedules from 'components/MonitoringAgent/hooks/useMonitoringSchedules';
import MonitoringSchedulesModal from 'components/MonitoringAgent/MonitoringSchedulesModal/MonitoringSchedulesModal';
import { buildScheduleRecurrenceSummary } from 'components/MonitoringAgent/utils/scheduleRecurrenceSummary';
import SmallLoader from 'components/SmallLoader/SmallLoader';
import TextField from 'components/TextField/TextField';
import { VALUE_CHAIN_MONITORING_AGENT } from 'constants/general';
import DeleteModal from 'pages/CompanyAdminPages/ValueChainMap/components/DeleteModal/DeleteModal';

import type IMonitoringSchedule from 'constants/interfaces/IMonitoringSchedule';
import type IMonitoringSchedulesTab from './interfaces/IMonitoringSchedulesTab';

import styles from './sass/MonitoringSchedulesTab.module.scss';

const formatDateTime = (value?: string): string => {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusClassName = (status?: string): string => {
  switch (status) {
    case 'completed':
      return styles.statusCompleted;
    case 'running':
      return styles.statusRunning;
    case 'failed':
      return styles.statusFailed;
    default:
      return styles.statusSkipped;
  }
};

function MonitoringSchedulesTab({
  schedules,
  sessionType,
  selectionMode = 'upstream-suppliers',
  schedulesInfoKey,
  valueChainMapId,
  isLoading = false,
  isError = false,
  isActionLoading = false,
  onNewSchedule,
  onOpenSchedule,
  onEditSchedule,
  onDeleteSchedule,
  onPauseSchedule,
  onResumeSchedule,
}: IMonitoringSchedulesTab): React.JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [isSchedulesModalOpen, setIsSchedulesModalOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<IMonitoringSchedule | undefined>(undefined);
  const usesProductCount = selectionMode === 'products-mixed'
    || sessionType === VALUE_CHAIN_MONITORING_AGENT.REGULATION_SESSION_TYPE;

  const { filteredSchedules } = useMonitoringSchedules({
    schedules,
    sessionType,
    search,
  });

  const handleDeleteClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    schedule: IMonitoringSchedule,
  ) => {
    event.stopPropagation();
    setScheduleToDelete(schedule);
  };

  const handleConfirmDelete = () => {
    if (scheduleToDelete) {
      onDeleteSchedule(scheduleToDelete);
    }

    setScheduleToDelete(undefined);
  };

  const handleEditFromModal = (schedule: IMonitoringSchedule) => {
    setIsSchedulesModalOpen(false);
    onEditSchedule(schedule);
  };

  const handleOpenRunHistoryFromModal = (schedule: IMonitoringSchedule) => {
    setIsSchedulesModalOpen(false);
    onOpenSchedule(schedule);
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.infoBox}>
          <InfoIcon aria-hidden="true" />
          <p className={styles.description}>
            {t(schedulesInfoKey)}
          </p>
        </div>

        <div className={styles.analysisCtaCard}>
          <div className={styles.analysisCtaCopy}>
            <p className={styles.analysisCtaHint}>
              {t('valueChainMap.monitoringSchedulesOpenModalHint')}
            </p>
            <button
              type="button"
              className={styles.analysisButtonPrimary}
              onClick={() => setIsSchedulesModalOpen(true)}
              disabled={isLoading || isActionLoading}
            >
              {t('valueChainMap.monitoringSchedulesOpenModal')}
            </button>
          </div>
        </div>

        <TextField
          value={search}
          name="monitoringSchedulesSearch"
          placeholder={t('valueChainMap.monitoringHubSchedulesSearchPlaceholder')}
          icon={<SearchIcon />}
          className={styles.searchField}
          onChange={({ target }) => setSearch(target.value)}
        />

        <button
          type="button"
          className={styles.newScheduleButton}
          onClick={onNewSchedule}
          disabled={isActionLoading}
          data-i18n-key="valueChainMap.monitoringHubNewSchedule"
        >
          <PlusIcon aria-hidden="true" />
          <span>{t('valueChainMap.monitoringHubNewSchedule')}</span>
        </button>

        <div className={styles.schedulesList}>
          {isLoading ? (
            <div className={styles.loaderWrapper}>
              <SmallLoader size={30} />
            </div>
          ) : null}

          {!isLoading && isError ? (
            <p className={styles.stateMessage}>{t('valueChainMap.monitoringSchedulesLoadError')}</p>
          ) : null}

          {!isLoading && !isError && !filteredSchedules.length ? (
            <p className={styles.stateMessage}>{t('valueChainMap.monitoringSchedulesEmpty')}</p>
          ) : null}

          {!isLoading && !isError && filteredSchedules.map((schedule) => {
            const isResumeBlocked = schedule.paused && schedule.agentConfigId == null;

            return (
              <article
                key={schedule.id}
                className={`${styles.scheduleCard}${schedule.paused ? ` ${styles.scheduleCardPaused}` : ''}`}
              >
                <div className={styles.scheduleCardHeader}>
                  <button
                    type="button"
                    className={styles.scheduleCardMain}
                    onClick={() => onOpenSchedule(schedule)}
                    disabled={isActionLoading}
                  >
                    <h3 className={styles.scheduleTitle}>
                      {schedule.title}
                      {schedule.paused ? (
                        <span className={styles.pausedBadge}>
                          {t('valueChainMap.monitoringSchedulePausedBadge')}
                        </span>
                      ) : null}
                    </h3>
                    <p className={styles.scheduleMeta}>
                      {buildScheduleRecurrenceSummary(schedule.schedule)}
                      {' · '}
                      {schedule.schedule.timezone}
                      {' · '}
                      {t(
                        usesProductCount
                          ? 'valueChainMap.monitoringScheduleProductCount'
                          : 'valueChainMap.monitoringScheduleEntityCount',
                        {
                          count: schedule.unitIds.length,
                        },
                      )}
                    </p>
                    {schedule.agentConfigId == null ? (
                      <p className={styles.missingTemplateHint}>
                        {t('valueChainMap.monitoringScheduleMissingTemplate')}
                      </p>
                    ) : null}
                  </button>
                  <div className={styles.scheduleActions}>
                    <button
                      type="button"
                      className={styles.textActionButton}
                      onClick={(event) => {
                        event.stopPropagation();
                        onEditSchedule(schedule);
                      }}
                      disabled={isActionLoading}
                      aria-label={t('valueChainMap.monitoringScheduleEdit')}
                    >
                      {t('valueChainMap.monitoringScheduleEdit')}
                    </button>
                    <button
                      type="button"
                      className={styles.textActionButton}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (schedule.paused) {
                          onResumeSchedule(schedule);
                        } else {
                          onPauseSchedule(schedule);
                        }
                      }}
                      disabled={isActionLoading || isResumeBlocked}
                      title={isResumeBlocked
                        ? t('valueChainMap.monitoringScheduleResumeUnavailable')
                        : undefined}
                      aria-label={t(
                        schedule.paused
                          ? 'valueChainMap.monitoringScheduleResume'
                          : 'valueChainMap.monitoringSchedulePause',
                      )}
                      data-i18n-key={schedule.paused
                        ? 'valueChainMap.monitoringScheduleResume'
                        : 'valueChainMap.monitoringSchedulePause'}
                    >
                      {t(
                        schedule.paused
                          ? 'valueChainMap.monitoringScheduleResume'
                          : 'valueChainMap.monitoringSchedulePause',
                      )}
                    </button>
                    <button
                      type="button"
                      className={styles.iconActionButton}
                      onClick={() => onOpenSchedule(schedule)}
                      disabled={isActionLoading}
                      aria-label={t('valueChainMap.monitoringScheduleOpenRunHistory')}
                      data-i18n-key="valueChainMap.monitoringScheduleOpenRunHistory"
                    >
                      <ChevronIcon aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className={styles.iconActionButton}
                      onClick={(event) => handleDeleteClick(event, schedule)}
                      disabled={isActionLoading}
                      aria-label={t('valueChainMap.monitoringScheduleDelete')}
                      data-i18n-key="valueChainMap.monitoringScheduleDelete"
                    >
                      <TrashIcon aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <div className={styles.entityChips}>
                  {(schedule.entities || []).map((entity) => (
                    <span key={entity.unitId} className={styles.entityChip}>
                      {entity.name}
                    </span>
                  ))}
                </div>

                <div className={styles.runMetaRow}>
                  <span>
                    <strong>
                      {t('valueChainMap.monitoringScheduleNextRun')}
                      :
                    </strong>
                    {' '}
                    {formatDateTime(schedule.nextRunAt)}
                  </span>
                  <span>
                    <strong>
                      {t('valueChainMap.monitoringScheduleLastRun')}
                      :
                    </strong>
                    {' '}
                    <span className={`${styles.statusBadge} ${getStatusClassName(schedule.lastRunStatus)}`}>
                      {schedule.lastRunStatus || 'skipped'}
                    </span>
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <MonitoringSchedulesModal
        open={isSchedulesModalOpen}
        valueChainMapId={valueChainMapId}
        schedules={schedules}
        isSchedulesLoading={isLoading}
        isSchedulesError={isError}
        onClose={() => setIsSchedulesModalOpen(false)}
        onEditSchedule={handleEditFromModal}
        onOpenRunHistory={handleOpenRunHistoryFromModal}
      />

      <DeleteModal
        open={Boolean(scheduleToDelete)}
        onReject={() => setScheduleToDelete(undefined)}
        isLoading={isActionLoading}
        onConfirm={handleConfirmDelete}
        titleKey="valueChainMap.monitoringScheduleDeleteModalTitle"
        hintKey="valueChainMap.monitoringScheduleDeleteModalDescription"
        name={scheduleToDelete?.title || ''}
      />
    </>
  );
}

export default MonitoringSchedulesTab;
